// Typed API-client voor de Sprekersmodule. Hergebruikt de globale axios-
// instance uit AuthContext (Authorization-header wordt daar via een
// interceptor gezet) -- geen eigen axios-instantie nodig.
import axios from 'axios';
import type {
  CanvasBoardDetail,
  CanvasBoardImportInput,
  CanvasBoardUpdateInput,
  CanvasFrame,
  CanvasFrameCreateInput,
  CanvasFrameUpdateInput,
  CanvasTemplate,
  CanvasTemplateInput,
  CanvasTemplateSummary,
  PresentationDetail,
  PresentationInput,
  PresentationSession,
  PresentationSlide,
  PresentationSlideInput,
  PresentationTemplate,
  PresentationTemplateInput,
  PresentationTemplateSlide,
  PresentationUpdateInput,
  SermonContent,
  SermonContentInput,
  SessionStreamToken,
  SpeakerInvitationInput,
  SpeakerMedia,
  SpeakerNote,
  SpeakerNoteInput,
  SpeakerProfile,
  SpeakerProfileInput,
  SpeakingEngagement,
  SpeakingEngagementInput,
} from './types';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/sprekers`;

export const sprekersApi = {
  listProfiles: () => axios.get<SpeakerProfile[]>(`${BASE}/profiles`).then((r) => r.data),
  createProfile: (data: SpeakerProfileInput) => axios.post<SpeakerProfile>(`${BASE}/profiles`, data).then((r) => r.data),
  updateProfile: (id: string, data: Partial<SpeakerProfileInput>) =>
    axios.put<SpeakerProfile>(`${BASE}/profiles/${id}`, data).then((r) => r.data),

  listEngagements: () => axios.get<SpeakingEngagement[]>(`${BASE}/engagements`).then((r) => r.data),
  getEngagement: (id: string) => axios.get<SpeakingEngagement>(`${BASE}/engagements/${id}`).then((r) => r.data),
  createEngagement: (data: SpeakingEngagementInput) =>
    axios.post<SpeakingEngagement>(`${BASE}/engagements`, data).then((r) => r.data),
  updateEngagement: (id: string, data: Partial<SpeakingEngagementInput>) =>
    axios.put<SpeakingEngagement>(`${BASE}/engagements/${id}`, data).then((r) => r.data),
  deleteEngagement: (id: string) => axios.delete(`${BASE}/engagements/${id}`),

  inviteSpeaker: (engagementId: string, data: SpeakerInvitationInput) =>
    axios.post<SpeakingEngagement>(`${BASE}/engagements/${engagementId}/invite`, data).then((r) => r.data),
  acceptInvitation: (engagementId: string, responseMessage?: string | null) =>
    axios
      .post<SpeakingEngagement>(`${BASE}/engagements/${engagementId}/accept`, { response_message: responseMessage || null })
      .then((r) => r.data),
  declineInvitation: (engagementId: string, responseMessage?: string | null) =>
    axios
      .post<SpeakingEngagement>(`${BASE}/engagements/${engagementId}/decline`, { response_message: responseMessage || null })
      .then((r) => r.data),

  listContent: (engagementId: string) => axios.get<SermonContent[]>(`${BASE}/engagements/${engagementId}/content`).then((r) => r.data),
  createContent: (engagementId: string, data: SermonContentInput) =>
    axios.post<SermonContent>(`${BASE}/engagements/${engagementId}/content`, data).then((r) => r.data),
  updateContent: (contentId: string, data: Partial<SermonContentInput>) =>
    axios.put<SermonContent>(`${BASE}/content/${contentId}`, data).then((r) => r.data),

  listNotes: (engagementId: string) => axios.get<SpeakerNote[]>(`${BASE}/engagements/${engagementId}/notes`).then((r) => r.data),
  createNote: (engagementId: string, data: SpeakerNoteInput) =>
    axios.post<SpeakerNote>(`${BASE}/engagements/${engagementId}/notes`, data).then((r) => r.data),
  updateNote: (noteId: string, data: Partial<SpeakerNoteInput>) =>
    axios.put<SpeakerNote>(`${BASE}/notes/${noteId}`, data).then((r) => r.data),
  deleteNote: (noteId: string) => axios.delete(`${BASE}/notes/${noteId}`),

  listTemplates: () => axios.get<PresentationTemplate[]>(`${BASE}/templates`).then((r) => r.data),
  createTemplate: (data: PresentationTemplateInput) =>
    axios.post<PresentationTemplate>(`${BASE}/templates`, data).then((r) => r.data),
  updateTemplate: (id: string, data: Partial<PresentationTemplateInput>) =>
    axios.put<PresentationTemplate>(`${BASE}/templates/${id}`, data).then((r) => r.data),
  deleteTemplate: (id: string) => axios.delete(`${BASE}/templates/${id}`),
  getTemplateSlides: (id: string) =>
    axios.get<PresentationTemplateSlide[]>(`${BASE}/templates/${id}/slides`).then((r) => r.data),
  // Google Slides als sjabloon opslaan: zelfde tokenregels als de presentatie-import (alleen in de body).
  importGoogleSlidesTemplate: (data: { file_id: string; access_token: string; name?: string; description?: string; mode?: 'visual' | 'editable' }) =>
    axios.post<PresentationTemplate>(`${BASE}/templates/import-google-slides`, data).then((r) => r.data),
  // Nieuwe, zelfstandige presentatie uit een slides-sjabloon (kopieert de afbeeldingen).
  createPresentationFromTemplate: (engagementId: string, data: { template_id: string; title?: string }) =>
    axios.post<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation/from-template`, data).then((r) => r.data),

  getPresentation: (engagementId: string) =>
    axios.get<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation`).then((r) => r.data),
  createPresentation: (engagementId: string, data: PresentationInput) =>
    axios.post<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation`, data).then((r) => r.data),
  // PDF-import: elke pagina wordt server-side een afbeeldingsslide (backend/sprekers/pdf_import.py).
  importPdfPresentation: (engagementId: string, file: File, title?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    return axios
      .post<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation/import-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  // Server-berekende toegang tot de Google Slides-import (pilot-allowlist, standaard false).
  getGoogleSlidesStatus: () =>
    axios.get<{ enabled: boolean }>(`${BASE}/google-slides/status`).then((r) => r.data),
  // Google Slides-import: het kortlevende access-token gaat uitsluitend in de body
  // (nooit in een URL) en wordt door de backend niet bewaard of gelogd.
  importGoogleSlidesPresentation: (engagementId: string, data: { file_id: string; access_token: string; title?: string }) =>
    axios.post<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation/import-google-slides`, data).then((r) => r.data),
  // Bewerkbare import (Slides API): echte tekst/afbeeldingen/vormen i.p.v. gerenderde pagina's.
  importGoogleSlidesEditable: (engagementId: string, data: { file_id: string; access_token: string; title?: string }) =>
    axios.post<PresentationDetail>(`${BASE}/engagements/${engagementId}/presentation/import-google-slides-editable`, data).then((r) => r.data),
  // Hersync vervangt de slides; confirm_replace moet expliciet true zijn (ook server-side gecontroleerd).
  resyncGoogleSlidesPresentation: (presentationId: string, accessToken: string) =>
    axios
      .post<PresentationDetail>(`${BASE}/presentations/${presentationId}/resync-google-slides`, {
        access_token: accessToken, confirm_replace: true,
      })
      .then((r) => r.data),
  updatePresentation: (presentationId: string, data: PresentationUpdateInput) =>
    axios.put(`${BASE}/presentations/${presentationId}`, data).then((r) => r.data),

  // ============== Canvas Player (Stap 1) ==============
  getCanvasBoard: (presentationId: string) =>
    axios.get<CanvasBoardDetail>(`${BASE}/presentations/${presentationId}/canvas`).then((r) => r.data),
  importCanvas: (presentationId: string, data: CanvasBoardImportInput) =>
    axios.post<CanvasBoardDetail>(`${BASE}/presentations/${presentationId}/canvas/import`, data).then((r) => r.data),

  // ============== Canvas Editor (Stap 2) ==============
  createEmptyCanvasBoard: (presentationId: string) =>
    axios.post<CanvasBoardDetail>(`${BASE}/presentations/${presentationId}/canvas`).then((r) => r.data),
  updateCanvasBoard: (presentationId: string, data: CanvasBoardUpdateInput) =>
    axios.put<CanvasBoardDetail>(`${BASE}/presentations/${presentationId}/canvas`, data).then((r) => r.data),
  createCanvasFrame: (presentationId: string, data: CanvasFrameCreateInput) =>
    axios.post<CanvasFrame>(`${BASE}/presentations/${presentationId}/canvas/frames`, data).then((r) => r.data),
  updateCanvasFrame: (presentationId: string, frameId: string, data: CanvasFrameUpdateInput) =>
    axios.put<CanvasFrame>(`${BASE}/presentations/${presentationId}/canvas/frames/${frameId}`, data).then((r) => r.data),
  deleteCanvasFrame: (frameId: string) =>
    axios.delete<CanvasBoardDetail>(`${BASE}/canvas/frames/${frameId}`).then((r) => r.data),
  applyCanvasTemplate: (presentationId: string, templateRef: string) =>
    axios
      .post<CanvasBoardDetail>(`${BASE}/presentations/${presentationId}/canvas/apply-template`, { template_ref: templateRef })
      .then((r) => r.data),
  listCanvasTemplates: () => axios.get<CanvasTemplateSummary[]>(`${BASE}/canvas-templates`).then((r) => r.data),
  createCanvasTemplate: (data: CanvasTemplateInput) =>
    axios.post<CanvasTemplate>(`${BASE}/canvas-templates`, data).then((r) => r.data),
  deleteCanvasTemplate: (templateId: string) => axios.delete(`${BASE}/canvas-templates/${templateId}`),

  createSlide: (presentationId: string, data: PresentationSlideInput) =>
    axios.post<PresentationSlide>(`${BASE}/presentations/${presentationId}/slides`, data).then((r) => r.data),
  updateSlide: (slideId: string, data: Partial<PresentationSlideInput>) =>
    axios.put<PresentationSlide>(`${BASE}/slides/${slideId}`, data).then((r) => r.data),
  deleteSlide: (slideId: string) => axios.delete(`${BASE}/slides/${slideId}`),
  duplicateSlide: (slideId: string) =>
    axios.post<PresentationSlide>(`${BASE}/slides/${slideId}/duplicate`).then((r) => r.data),
  reorderSlides: (presentationId: string, slideIds: string[]) =>
    axios.put<PresentationSlide[]>(`${BASE}/presentations/${presentationId}/slides/order`, { slide_ids: slideIds }).then((r) => r.data),
  saveAsTemplate: (presentationId: string, data: { name: string; description?: string }) =>
    axios.post<PresentationTemplate>(`${BASE}/presentations/${presentationId}/save-as-template`, data).then((r) => r.data),

  uploadMedia: (file: File, options: { speakingEngagementId?: string; title?: string } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.speakingEngagementId) formData.append('speaking_engagement_id', options.speakingEngagementId);
    if (options.title) formData.append('title', options.title);
    return axios
      .post<SpeakerMedia>(`${BASE}/media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  downloadMedia: async (mediaId: string, filename: string) => {
    // authenticatie loopt via de axios-interceptor, dus geen kale <a href> --
    // zelfde patroon als leiderschapApi.downloadDocument.
    const response = await axios.get(`${BASE}/media/${mediaId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  deleteMedia: (mediaId: string) => axios.delete(`${BASE}/media/${mediaId}`),

  // Media inline weergeven (beamer/telefoon) i.p.v. downloaden -- zelfde
  // geauthenticeerde endpoint als downloadMedia, maar levert een blob-URL op
  // voor gebruik als <img>/<video>/<audio> src.
  getMediaBlobUrl: async (mediaId: string) => {
    const response = await axios.get(`${BASE}/media/${mediaId}/download`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data]));
  },

  // ============== Fase 3: live spreekmodus ==============
  createSession: (presentationId: string) =>
    axios.post<PresentationSession>(`${BASE}/presentations/${presentationId}/sessions`).then((r) => r.data),
  connectSession: (sessionId: string, sessionToken: string) =>
    axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/connect`, { session_token: sessionToken }).then((r) => r.data),
  nextSlide: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/next`).then((r) => r.data),
  previousSlide: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/previous`).then((r) => r.data),
  // Canvas-presentaties gebruiken padnavigatie i.p.v. next/previousSlide
  // (besluit #3: alleen betrouwbare pad-navigatie, geen vrij scrollen).
  nextStep: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/step/next`).then((r) => r.data),
  previousStep: (sessionId: string) =>
    axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/step/previous`).then((r) => r.data),
  gotoStep: (sessionId: string, targetId: string) =>
    axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/step/goto`, { target_id: targetId }).then((r) => r.data),
  gotoTheme: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/step/theme`).then((r) => r.data),
  toggleCanvasOverview: (sessionId: string) =>
    axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/step/overview`).then((r) => r.data),
  toggleBlackout: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/blackout`).then((r) => r.data),
  endSession: (sessionId: string) => axios.post<PresentationSession>(`${BASE}/sessions/${sessionId}/end`).then((r) => r.data),
  // Sessiegebonden stream-token voor de SSE-route (nooit het gewone
  // gebruikers-JWT, zie backend/sprekers/service.py create_stream_token).
  createStreamToken: (sessionId: string) =>
    axios.post<SessionStreamToken>(`${BASE}/sessions/${sessionId}/stream-token`).then((r) => r.data),
  // De browser's EventSource kan geen Authorization-header zetten, dus het
  // stream-token gaat als query-param mee. Dit is NIET het gebruikers-JWT --
  // uitsluitend het kortlevende token uit createStreamToken.
  sessionEventsUrl: (sessionId: string, streamToken: string | null) =>
    `${BASE}/sessions/${sessionId}/events${streamToken ? `?token=${encodeURIComponent(streamToken)}` : ''}`,
};
